import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../services/api";
import {
  createTaskReminderManager,
  deliverTaskReminder,
  fromLocalDateTimeInput,
  REMINDER_SOUND_KEY,
  taskStatus,
  toLocalDateTimeInput,
} from "../utils/taskReminders";
import { isAndroidApp } from "../platform";
import {
  installAndroidReminderActionHandler,
  PENDING_TASK_KEY,
  reconcileAndroidLocalReminders,
  requestAndroidReminderPermission,
} from "../utils/backgroundTaskReminders";

const PALETTE = ["#0d6efd", "#198754", "#f5820d", "#dc2626", "#6f42c1", "#0891b2", "#db2777", "#0f172a"];
const DAYS = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];
const DAY_LABEL = { lundi: "Lundi", mardi: "Mardi", mercredi: "Mercredi", jeudi: "Jeudi", vendredi: "Vendredi", samedi: "Samedi", dimanche: "Dimanche" };
const EMPTY_TASK = { text: "", due: "", reminder: false };
const EMPTY_SLOT = { jour: "lundi", heure_debut: "08:00", heure_fin: "09:00", label: "", couleur: "#0d6efd" };

const apiMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (!data) return fallback;
  const value = data.non_field_errors || data.heure_fin || data.reminder_enabled || data.detail;
  return Array.isArray(value) ? value[0] : (value || fallback);
};

const formatDue = value => value ? new Intl.DateTimeFormat("fr-FR", {
  dateStyle: "medium", timeStyle: "short",
}).format(new Date(value)) : "Sans échéance";

function playShortSound() {
  const AudioContextApi = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextApi) return;
  const context = new AudioContextApi();
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.frequency.value = 760;
  gain.gain.setValueAtTime(0.08, context.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.16);
  oscillator.connect(gain).connect(context.destination);
  oscillator.start();
  oscillator.stop(context.currentTime + 0.16);
  oscillator.onended = () => context.close();
}

export default function NotesOrganizationView() {
  const [tab, setTab] = useState("todo");
  const [todos, setTodos] = useState([]);
  const [todoLoad, setTodoLoad] = useState(true);
  const [taskForm, setTaskForm] = useState(EMPTY_TASK);
  const [editTask, setEditTask] = useState(null);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [taskError, setTaskError] = useState("");
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem(REMINDER_SOUND_KEY) !== "false");
  const [permission, setPermission] = useState(() => window.Notification?.permission || "unsupported");
  const [reminderToast, setReminderToast] = useState(null);
  const reminderManager = useRef(null);

  const [slots, setSlots] = useState([]);
  const [slotLoad, setSlotLoad] = useState(true);
  const [showSlotForm, setShowSlotForm] = useState(false);
  const [editSlot, setEditSlot] = useState(null);
  const [slotForm, setSlotForm] = useState(EMPTY_SLOT);
  const [slotError, setSlotError] = useState("");
  const [mobileDay, setMobileDay] = useState(DAYS[(new Date().getDay() + 6) % 7]);

  useEffect(() => {
    api.get("todos/").then(r => setTodos(r.data)).catch(() => setTaskError("Les tâches n’ont pas pu être chargées.")).finally(() => setTodoLoad(false));
    api.get("emploi-du-temps/").then(r => setSlots(r.data)).catch(() => setSlotError("L’emploi du temps n’a pas pu être chargé.")).finally(() => setSlotLoad(false));
  }, []);

  const openTask = task => {
    setTab("todo");
    window.focus?.();
    setTimeout(() => document.getElementById(`ql-task-${task.id}`)?.focus(), 0);
  };

  useEffect(() => {
    reminderManager.current = createTaskReminderManager({
      storage: localStorage,
      notify: task => deliverTaskReminder({
        task,
        NotificationApi: window.Notification,
        onInApp: setReminderToast,
        onOpen: openTask,
      }),
      playSound: () => { try { playShortSound(); } catch (_) {} },
    });
    return () => reminderManager.current?.cancelAll();
  }, []);

  useEffect(() => {
    reminderManager.current?.sync(todos, { soundEnabled });
    if (isAndroidApp) {
      reconcileAndroidLocalReminders({ tasks: todos, plugin: window.Capacitor?.Plugins?.LocalNotifications, soundEnabled })
        .then(result => result.permission && setPermission(result.permission))
        .catch(() => setPermission("unsupported"));
    }
  }, [todos, soundEnabled]);

  useEffect(() => {
    if (!isAndroidApp) return undefined;
    const openPending = event => {
      const taskId = Number(event?.detail?.taskId || localStorage.getItem(PENDING_TASK_KEY));
      const task = todos.find(item => item.id === taskId);
      if (task) { localStorage.removeItem(PENDING_TASK_KEY); openTask(task); }
    };
    window.addEventListener("ql-open-reminder-task", openPending);
    const listener = installAndroidReminderActionHandler({ plugin: window.Capacitor?.Plugins?.LocalNotifications, storage: localStorage });
    openPending();
    return () => { window.removeEventListener("ql-open-reminder-task", openPending); listener?.then?.(handle => handle.remove()); };
  }, [todos]);

  const groupedTasks = useMemo(() => {
    const groups = { overdue: [], upcoming: [], undated: [], completed: [] };
    todos.forEach(task => groups[taskStatus(task)].push(task));
    Object.values(groups).forEach(items => items.sort((a, b) => (a.due_at || "9999").localeCompare(b.due_at || "9999")));
    return groups;
  }, [todos]);

  const slotsByDay = useMemo(() => DAYS.reduce((result, day) => {
    result[day] = slots.filter(slot => slot.jour === day).sort((a, b) => a.heure_debut.localeCompare(b.heure_debut));
    return result;
  }, {}), [slots]);
  const timeRows = useMemo(() => [...new Set(slots.map(slot => slot.heure_debut.slice(0, 5)))].sort(), [slots]);

  const startTask = task => {
    setEditTask(task || null);
    setTaskForm(task ? { text: task.text, due: toLocalDateTimeInput(task.due_at), reminder: task.reminder_enabled } : EMPTY_TASK);
    setTaskError("");
    setShowTaskForm(true);
  };

  const saveTask = async event => {
    event.preventDefault();
    setTaskError("");
    const payload = {
      text: taskForm.text.trim(),
      due_at: fromLocalDateTimeInput(taskForm.due),
      reminder_enabled: Boolean(taskForm.due && taskForm.reminder),
      ...(!editTask ? { completed: false, order: todos.length } : {}),
    };
    try {
      const response = editTask ? await api.patch(`todos/${editTask.id}/`, payload) : await api.post("todos/", payload);
      setTodos(previous => editTask ? previous.map(item => item.id === response.data.id ? response.data : item) : [...previous, response.data]);
      setShowTaskForm(false);
    } catch (error) { setTaskError(apiMessage(error, "La tâche n’a pas pu être enregistrée.")); }
  };

  const patchTask = async (task, payload) => {
    try {
      const response = await api.patch(`todos/${task.id}/`, payload);
      setTodos(previous => previous.map(item => item.id === response.data.id ? response.data : item));
    } catch (_) { setTaskError("La tâche n’a pas pu être mise à jour."); }
  };

  const deleteTask = async id => {
    try { await api.delete(`todos/${id}/`); setTodos(previous => previous.filter(item => item.id !== id)); }
    catch (_) { setTaskError("La tâche n’a pas pu être supprimée."); }
  };

  const setSound = enabled => {
    setSoundEnabled(enabled);
    localStorage.setItem(REMINDER_SOUND_KEY, String(enabled));
  };

  const askPermission = async () => {
    if (isAndroidApp) {
      setPermission(await requestAndroidReminderPermission(window.Capacitor?.Plugins?.LocalNotifications));
      return;
    }
    if (!window.Notification) return;
    const result = await window.Notification.requestPermission();
    setPermission(result);
  };

  const startSlot = slot => {
    setEditSlot(slot || null);
    setSlotForm(slot ? { ...slot, heure_debut: slot.heure_debut.slice(0, 5), heure_fin: slot.heure_fin.slice(0, 5) } : EMPTY_SLOT);
    setSlotError("");
    setShowSlotForm(true);
  };

  const saveSlot = async event => {
    event.preventDefault();
    setSlotError("");
    try {
      const response = editSlot ? await api.put(`emploi-du-temps/${editSlot.id}/`, slotForm) : await api.post("emploi-du-temps/", slotForm);
      setSlots(previous => editSlot ? previous.map(item => item.id === response.data.id ? response.data : item) : [...previous, response.data]);
      setShowSlotForm(false);
    } catch (error) { setSlotError(apiMessage(error, "Le créneau n’a pas pu être enregistré.")); }
  };

  const deleteSlot = async id => {
    try { await api.delete(`emploi-du-temps/${id}/`); setSlots(previous => previous.filter(item => item.id !== id)); }
    catch (_) { setSlotError("Le créneau n’a pas pu être supprimé."); }
  };

  const renderTaskGroup = (key, title) => groupedTasks[key].length > 0 && (
    <section className={`ql-task-group ql-task-${key}`}>
      <h4>{title} <span>{groupedTasks[key].length}</span></h4>
      {groupedTasks[key].map(task => (
        <article id={`ql-task-${task.id}`} tabIndex="-1" key={task.id} className="ql-task-card">
          <button className="ql-todo-check" onClick={() => patchTask(task, { completed: !task.completed })} aria-label={task.completed ? "Rouvrir" : "Terminer"}>
            <i className={`bi bi-${task.completed ? "check-circle-fill" : "circle"}`}></i>
          </button>
          <div className="ql-task-content">
            <strong>{task.text}</strong>
            <small><i className="bi bi-clock me-1"></i>{formatDue(task.due_at)}{task.reminder_enabled && <span className="ms-2"><i className="bi bi-bell-fill"></i> Rappel</span>}</small>
          </div>
          <button onClick={() => startTask(task)} title="Modifier"><i className="bi bi-pencil"></i></button>
          <button onClick={() => deleteTask(task.id)} title="Supprimer"><i className="bi bi-trash3"></i></button>
        </article>
      ))}
    </section>
  );

  const renderSlot = slot => (
    <div key={slot.id} className="ql-edt-slot" style={{ borderLeftColor: slot.couleur }}>
      <div className="ql-edt-slot-time" style={{ color: slot.couleur }}>{slot.heure_debut.slice(0, 5)} – {slot.heure_fin.slice(0, 5)}</div>
      <div className="ql-edt-slot-label">{slot.label}</div>
      <div className="ql-edt-slot-actions">
        <button onClick={() => startSlot(slot)} title="Modifier"><i className="bi bi-pencil"></i></button>
        <button onClick={() => deleteSlot(slot.id)} title="Supprimer"><i className="bi bi-trash3"></i></button>
      </div>
    </div>
  );

  return <div className="ql-organizer">
    <div className="ql-view-header"><h3 className="ql-view-title"><i className="bi bi-calendar-check me-2"></i>Notes & Organisation</h3></div>
    <div className="ql-result-tabs mb-4">
      <button className={`ql-result-tab${tab === "todo" ? " active" : ""}`} onClick={() => setTab("todo")}><i className="bi bi-check2-square me-1"></i>Tâches</button>
      <button className={`ql-result-tab${tab === "edt" ? " active" : ""}`} onClick={() => setTab("edt")}><i className="bi bi-calendar-week me-1"></i>Emploi du temps</button>
    </div>

    {reminderToast && <div className="ql-reminder-toast" role="alert">
      <i className="bi bi-bell-fill"></i><div><strong>Rappel</strong><br />{reminderToast.text}</div>
      <button onClick={() => { openTask(reminderToast); setReminderToast(null); }}>Voir</button>
      <button aria-label="Fermer" onClick={() => setReminderToast(null)}><i className="bi bi-x-lg"></i></button>
    </div>}

    {tab === "todo" && <div>
      <div className="ql-organizer-toolbar">
        <button className="btn btn-primary btn-sm" onClick={() => startTask()}><i className="bi bi-plus-lg me-1"></i>Nouvelle note ou tâche</button>
        <label className="ql-sound-toggle"><input type="checkbox" checked={soundEnabled} onChange={e => setSound(e.target.checked)} /> Son des rappels</label>
        {permission === "default" && <button className="btn btn-sm btn-outline-primary" onClick={askPermission}>Autoriser les notifications</button>}
        {permission === "denied" && <span className="ql-permission-note">Notifications système refusées : les rappels restent visibles dans l’application ouverte.</span>}
      </div>
      {taskError && <div className="alert alert-danger py-2">{taskError}</div>}
      {todoLoad ? <div className="text-center py-4"><span className="spinner-border spinner-border-sm text-primary"></span></div> : todos.length === 0
        ? <div className="ql-organizer-empty"><i className="bi bi-check2-all"></i><p>Aucune note ou tâche.</p></div>
        : <>{renderTaskGroup("overdue", "En retard")}{renderTaskGroup("upcoming", "À venir")}{renderTaskGroup("undated", "Sans échéance")}{renderTaskGroup("completed", "Terminées")}</>}
    </div>}

    {tab === "edt" && <div>
      <div className="ql-organizer-toolbar"><button className="btn btn-primary btn-sm" onClick={() => startSlot()}><i className="bi bi-plus-lg me-1"></i>Ajouter un créneau</button></div>
      {slotError && <div className="alert alert-danger py-2">{slotError}</div>}
      {slotLoad ? <div className="text-center py-4"><span className="spinner-border spinner-border-sm text-primary"></span></div> : slots.length === 0
        ? <div className="ql-organizer-empty"><i className="bi bi-calendar-week"></i><p>Ton emploi du temps est vide.</p></div>
        : <>
          <div className="ql-edt-weekly-wrap"><table className="ql-edt-weekly"><thead><tr><th>Horaire</th>{DAYS.map(day => <th key={day}>{DAY_LABEL[day]}</th>)}</tr></thead>
            <tbody>{timeRows.map(time => <tr key={time}><th>{time}</th>{DAYS.map(day => <td key={day}>{slotsByDay[day].filter(slot => slot.heure_debut.slice(0, 5) === time).map(renderSlot)}</td>)}</tr>)}</tbody>
          </table></div>
          <div className="ql-edt-daily"><label>Jour affiché<select value={mobileDay} onChange={e => setMobileDay(e.target.value)}>{DAYS.map(day => <option key={day} value={day}>{DAY_LABEL[day]}</option>)}</select></label>{slotsByDay[mobileDay].length ? slotsByDay[mobileDay].map(renderSlot) : <p className="text-muted mt-3">Aucun créneau ce jour.</p>}</div>
        </>}
    </div>}

    {showTaskForm && <div className="ql-modal-overlay" onClick={() => setShowTaskForm(false)}><div className="ql-modal" onClick={e => e.stopPropagation()}>
      <div className="ql-modal-header"><strong>{editTask ? "Modifier la tâche" : "Nouvelle note ou tâche"}</strong><button className="ql-modal-close" onClick={() => setShowTaskForm(false)}><i className="bi bi-x-lg"></i></button></div>
      <form className="ql-modal-body" onSubmit={saveTask}>
        {taskError && <div className="alert alert-danger py-2">{taskError}</div>}
        <label className="form-label fw-semibold">Note ou tâche</label><textarea className="form-control mb-3" required value={taskForm.text} onChange={e => setTaskForm(form => ({ ...form, text: e.target.value }))} />
        <label className="form-label fw-semibold">Échéance (facultative)</label><input className="form-control mb-3" type="datetime-local" value={taskForm.due} onChange={e => setTaskForm(form => ({ ...form, due: e.target.value, reminder: e.target.value ? form.reminder : false }))} />
        <label className="form-check mb-3"><input className="form-check-input" type="checkbox" disabled={!taskForm.due} checked={taskForm.reminder} onChange={e => setTaskForm(form => ({ ...form, reminder: e.target.checked }))} /><span className="form-check-label">Me rappeler à l’échéance</span></label>
        {!taskForm.due && <p className="small text-muted">Une note sans échéance est enregistrée normalement, sans rappel.</p>}
        <div className="d-flex justify-content-end gap-2"><button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setShowTaskForm(false)}>Annuler</button><button className="btn btn-sm btn-primary" disabled={!taskForm.text.trim()}>Enregistrer</button></div>
      </form></div></div>}

    {showSlotForm && <div className="ql-modal-overlay" onClick={() => setShowSlotForm(false)}><div className="ql-modal" onClick={e => e.stopPropagation()}>
      <div className="ql-modal-header"><strong>{editSlot ? "Modifier le créneau" : "Nouveau créneau"}</strong><button className="ql-modal-close" onClick={() => setShowSlotForm(false)}><i className="bi bi-x-lg"></i></button></div>
      <form className="ql-modal-body" onSubmit={saveSlot}>{slotError && <div className="alert alert-warning py-2">{slotError}</div>}
        <label className="form-label fw-semibold">Jour</label><select className="form-select mb-3" value={slotForm.jour} onChange={e => setSlotForm(form => ({ ...form, jour: e.target.value }))}>{DAYS.map(day => <option key={day} value={day}>{DAY_LABEL[day]}</option>)}</select>
        <label className="form-label fw-semibold">Matière / activité</label><input className="form-control mb-3" required value={slotForm.label} onChange={e => setSlotForm(form => ({ ...form, label: e.target.value }))} />
        <div className="row g-2 mb-3"><div className="col-6"><label className="form-label fw-semibold">Début</label><input type="time" className="form-control" required value={slotForm.heure_debut} onChange={e => setSlotForm(form => ({ ...form, heure_debut: e.target.value }))} /></div><div className="col-6"><label className="form-label fw-semibold">Fin</label><input type="time" className="form-control" required value={slotForm.heure_fin} onChange={e => setSlotForm(form => ({ ...form, heure_fin: e.target.value }))} /></div></div>
        <label className="form-label fw-semibold">Couleur</label><div className="ql-color-palette mb-3">{PALETTE.map(color => <button key={color} type="button" aria-label={`Couleur ${color}`} className={`ql-color-dot${slotForm.couleur === color ? " selected" : ""}`} style={{ background: color }} onClick={() => setSlotForm(form => ({ ...form, couleur: color }))} />)}</div>
        <div className="d-flex justify-content-end gap-2"><button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setShowSlotForm(false)}>Annuler</button><button className="btn btn-sm btn-primary">Enregistrer</button></div>
      </form></div></div>}
  </div>;
}
